import { FC, Fragment, useContext, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../../context/notificationContext";
import { createLoadOptions } from "../../../../../hooks/useDebounce";
import useI18n from "../../../../../hooks/useTranslations";
import { CustomError } from "../../../../../http/errors";
import { AdminCommonSteelVersion } from "../../../../../http/types/commonSteelVersions";
import { SearchAdminItem } from "../../../../../http/types/items";
import { useItemsApi } from "../../../../../http/useItems";
import { AdminInstallationType } from "../../../../../http/vertigrip/types/installationTypes";
import { AdminSystemType, isPassingThrough } from "../../../../../http/vertigrip/types/systemTypes";
import { useInstallationTypesApi } from "../../../../../http/vertigrip/useInstallationTypes";
import { useIntermediateElementsApi } from "../../../../../http/vertigrip/useIntermediateElements";
import { useVertigripSteelVersionsApi } from "../../../../../http/vertigrip/useSteelVersions";
import { useSystemTypesApi } from "../../../../../http/vertigrip/useSystemTypes";
import { ItemFixingWithQuantity } from "../../../../../types";
import Button from "../../../../ui/Button";
import ControlledNumericWithQuantity from "../../../../ui/ControlledNumericWithQuantity";
import Form from "../../../../ui/Form";
import FormField from "../../../../ui/FormField";
import FormFieldsContainer from "../../../../ui/FormFieldsContainer";
import Input from "../../../../ui/Input";
import Numeric from "../../../../ui/Numeric";
import { getBooleanValues } from "../../../../ui/select/BooleanValues";
import { CustomAsyncSelect, CustomSelect } from "../../../../ui/select/CustomSelect";
import VisibilityScope, { VisibilityScopeValue } from "../../../../ui/VisibilityScope";

interface IntermediateElementForm {
  item: SearchAdminItem | null;
  systemType: AdminSystemType | null;
  steelVersion: AdminCommonSteelVersion | null;
  installationType: AdminInstallationType | null;
  order: number | null;
  fixed: boolean;
  fixings: ItemFixingWithQuantity[];
  visibility: VisibilityScopeValue;
}

interface IntermediateElementFormProps {
  systemTypeId?: string;
  steelVersionId?: string;
  installationTypeId?: string;
  intermediateElementId?: string;
  onCancel: () => void;
  onSave?: (itemId: string) => void;
}

const IntermediateElementForm: FC<IntermediateElementFormProps> = ({
  systemTypeId,
  steelVersionId,
  installationTypeId,
  intermediateElementId,
  onCancel,
  onSave,
}: IntermediateElementFormProps) => {
  const i18n = useI18n();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useContext(NotificationDispatch);
  const aborter = useRef(new AbortController());
  const itemsApi = useItemsApi();
  const systemTypesApi = useSystemTypesApi();
  const installationTypesApi = useInstallationTypesApi();
  const steelVersionsApi = useVertigripSteelVersionsApi();
  const intermediateElementsApi = useIntermediateElementsApi();
  const [defaultOptions, setDefaultOptions] = useState<SearchAdminItem[]>([]);

  const [error, setError] = useState<CustomError>();

  const booleanValues = getBooleanValues(i18n.translation);

  const intermediateElementSwr = intermediateElementsApi.getAdminIntermediateElement(aborter.current.signal);

  const systemTypesSwr = systemTypesApi.useAdminSystemTypes({}, aborter.current.signal);
  const installationTypesSwr = installationTypesApi.useAdminInstallationTypes({}, aborter.current.signal);
  const steelVersionsSwr = steelVersionsApi.useAdminSteelVersions({}, aborter.current.signal);

  const steelVersions: AdminCommonSteelVersion[] = (() => {
    const items = steelVersionsSwr.data?.items ?? [];

    if (!intermediateElementSwr.data) {
      return items;
    }

    return !items.find((x) => x.id === intermediateElementSwr.data?.vertigripSteelVersion.id)
      ? [...items, intermediateElementSwr.data?.vertigripSteelVersion]
      : items;
  })();

  const installationTypes: AdminInstallationType[] = (() => {
    const items = installationTypesSwr.data?.items ?? [];

    if (!intermediateElementSwr.data) {
      return items;
    }

    return intermediateElementSwr.data?.installationType &&
      !items.find((x) => x.id === intermediateElementSwr.data?.installationType?.id)
      ? [...items, intermediateElementSwr.data.installationType]
      : items;
  })();

  useEffect(() => {
    const anyError =
      intermediateElementSwr.error || systemTypesSwr.error || installationTypesSwr.error || steelVersionsSwr.error;

    if (anyError) {
      setError(anyError); // Set the error
      dispatch(showErrorNotification(i18n, anyError)); // Trigger the notification
    } else {
      setError(undefined); // Clear the error if no errors
    }
  }, [intermediateElementSwr.error, systemTypesSwr.error, installationTypesSwr.error, steelVersionsSwr.error]);

  return (
    <Form<IntermediateElementForm>
      defaultValues={{
        fixed: intermediateElementSwr.data?.fixed ?? false,
        order: intermediateElementSwr.data?.order ?? 50,
        fixings: [],
        visibility: intermediateElementSwr.data?.visibility,
      }}
      submitText={i18n.translation.common.save}
      disabled={isSubmitting || !!error}
      secondaryButton={
        <Button
          buttonProps={{
            onClick: onCancel,
          }}
          type="secondary"
        >
          {i18n.translation.common.cancel}
        </Button>
      }
      onSubmit={() => async (formData) => {
        const body = {
          itemId: formData.item?.id,
          steelVersionId: formData.steelVersion?.id!,
          order: formData.order!,
          fixed: formData.fixed,
          installationTypeId: formData.installationType?.id,
          systemTypeId: formData.systemType?.id!,
          fixings: formData.installationType
            ? formData.fixings
                .filter((x) => !!x.quantity)
                .map((x) => ({
                  wallMaterialId: x.wallMaterial.id,
                  quantity: x.quantity ?? 0,
                }))
            : [],
          visibility: formData.visibility,
        };

        if (intermediateElementSwr.data?.id) {
          try {
            await intermediateElementsApi.updateAdminIntermediateElement(intermediateElementSwr.data.id, body);
            onCancel();
            dispatch(showSuccessNotification(i18n.translation));
          } catch (error) {
            dispatch(showErrorNotification(i18n, error));
          } finally {
            setIsSubmitting(false);
          }
        } else {
          try {
            const res = await intermediateElementsApi.insertAdminIntermediateElement({
              ...body,
              itemId: formData.item?.id!,
            });
            onSave?.(res.id);
            dispatch(showSuccessNotification(i18n.translation));
          } catch (error) {
            dispatch(showErrorNotification(i18n, error));
          } finally {
            setIsSubmitting(false);
          }
        }
      }}
    >
      {({ control, setValue, watch, disabled }) => {
        useEffect(() => {
          intermediateElementId && intermediateElementSwr.trigger(intermediateElementId);
        }, [intermediateElementId]);

        useEffect(() => {
          if (intermediateElementSwr.data) {
            setValue("order", intermediateElementSwr.data.order);
            setValue("fixed", intermediateElementSwr.data.fixed);
            setValue("visibility", intermediateElementSwr.data.visibility);
          }
        }, [intermediateElementSwr.data]);

        useEffect(() => {
          if (intermediateElementSwr.data) {
            const fixings: ItemFixingWithQuantity[] =
              installationTypesSwr.data?.items
                .find((x) => x.id === (intermediateElementSwr.data?.installationType?.id ?? installationTypeId))
                ?.wallMaterials.reduce((rv: ItemFixingWithQuantity[], wm) => {
                  const f = intermediateElementSwr.data?.fixings.find((x) => x.wallMaterialId === wm.id);
                  return [
                    ...rv,
                    {
                      wallMaterial: wm,
                      quantity: f?.quantity ?? 0,
                    },
                  ];
                }, []) ?? [];
            setValue("fixings", fixings);
          }
        }, [intermediateElementSwr.data, installationTypesSwr.data]);

        useEffect(() => {
          const steelVersion =
            steelVersions.find(
              (x) => x.id === (intermediateElementSwr.data?.vertigripSteelVersion.id ?? steelVersionId),
            ) ?? null;
          setValue("steelVersion", steelVersion);
        }, [steelVersions, intermediateElementSwr.data?.vertigripSteelVersion.id]);

        useEffect(() => {
          const installationType =
            installationTypes.find(
              (x) => x.id === (intermediateElementSwr.data?.installationType?.id ?? installationTypeId),
            ) ?? null;
          setValue("installationType", installationType);
        }, [installationTypes, intermediateElementSwr.data?.vertigripSteelVersion.id]);

        useEffect(() => {
          const systemType =
            systemTypesSwr.data?.items.find(
              (x) =>
                x.id === (intermediateElementSwr.data?.systemType.id ?? systemTypeId) ||
                (!isPassingThrough(x) && !!installationTypeId),
            ) ?? null;
          setValue("systemType", systemType);
        }, [systemTypesSwr.data, intermediateElementSwr.data?.vertigripSteelVersion.id]);

        const fixings =
          watch("installationType")?.wallMaterials.map((x, index) => ({
            wallMaterial: x,
            quantity: watch("fixings")[index]?.quantity ?? 0,
          })) ?? [];

        return (
          <FormFieldsContainer fullWidth>
            {!!intermediateElementSwr.data ? (
              <FormField label={i18n.translation.common.code}>
                <Input disabled value={intermediateElementSwr.data?.code} />
              </FormField>
            ) : (
              <Controller
                name="item"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => {
                  return (
                    <>
                      <FormField label={i18n.translation.items.singular} error={fieldState.error}>
                        {({ labelId }) => {
                          const searchTerm = useRef("");
                          const aborter = useRef(new AbortController());

                          const loadOptions = createLoadOptions(aborter, searchTerm, itemsApi.getAdminItems);

                          // Fetch initial data on mount
                          useEffect(() => {
                            loadOptions(searchTerm.current).then(setDefaultOptions);
                          }, []);

                          return (
                            <CustomAsyncSelect
                              {...field}
                              inputId={labelId}
                              defaultOptions={defaultOptions}
                              loadOptions={loadOptions}
                              isClearable
                              isDisabled={disabled}
                              getOptionLabel={(x) => x.code}
                              getOptionValue={(x) => x.id}
                              onChange={(value) => setValue("item", value ?? null)}
                            />
                          );
                        }}
                      </FormField>
                    </>
                  );
                }}
              />
            )}
            <Controller
              name="steelVersion"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.translation.steelVersions.singular}
                  error={fieldState.error}
                  hidden={!!steelVersionId}
                >
                  {({ labelId }) => (
                    <CustomSelect
                      {...field}
                      inputId={labelId}
                      options={steelVersions}
                      isLoading={steelVersionsSwr.isLoading}
                      isDisabled={!!steelVersionId || disabled}
                      getOptionValue={(x) => x.id}
                      getOptionLabel={(x) => x.name}
                      onChange={(selectedOption) => setValue("steelVersion", selectedOption ?? null)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="systemType"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.translation.systemTypes.singular}
                  error={fieldState.error}
                  hidden={!!systemTypeId}
                >
                  {({ labelId }) => (
                    <CustomSelect
                      {...field}
                      inputId={labelId}
                      options={systemTypesSwr.data?.items ?? []}
                      isLoading={systemTypesSwr.isLoading}
                      isDisabled={!!systemTypeId || !!installationTypeId || disabled}
                      getOptionValue={(x) => x.id}
                      getOptionLabel={(x) => x.name}
                      onChange={(selectedOption) => {
                        setValue("systemType", selectedOption ?? null);
                        isPassingThrough(selectedOption) && setValue("installationType", null);
                        setValue("fixed", false);
                      }}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="installationType"
              control={control}
              rules={{ required: !isPassingThrough(watch("systemType")) }}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.translation.installationTypes.singular}
                  error={fieldState.error}
                  hidden={isPassingThrough(watch("systemType")) || !!installationTypeId}
                >
                  {({ labelId }) => (
                    <CustomSelect
                      {...field}
                      inputId={labelId}
                      options={installationTypes}
                      isLoading={installationTypesSwr.isLoading}
                      isDisabled={isPassingThrough(watch("systemType")) || !!installationTypeId || disabled}
                      getOptionValue={(x) => x.id}
                      getOptionLabel={(x) => x.name}
                      onChange={(selectedOption) => {
                        setValue("installationType", selectedOption ?? null);
                        setValue(
                          "systemType",
                          systemTypesSwr.data?.items?.find((x) => isPassingThrough(x) === !selectedOption) ?? null,
                        );
                        setValue("fixed", false);
                      }}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="fixed"
              control={control}
              disabled={!isPassingThrough(watch("systemType")) || disabled}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.translation.installationTypes.singular}
                  error={fieldState.error}
                  hidden={!isPassingThrough(watch("systemType"))}
                >
                  {({ labelId }) => (
                    <CustomSelect
                      {...field}
                      inputId={labelId}
                      isDisabled={field.disabled}
                      options={booleanValues}
                      getOptionValue={(x) => x.value.toString()}
                      getOptionLabel={(x) =>
                        x.value ? i18n.translation.common.fixed : i18n.translation.common.removable
                      }
                      onChange={(selectedOption) => {
                        if (selectedOption?.value !== field.value) {
                          field.onChange(selectedOption?.value === true);
                        }
                      }}
                      value={booleanValues.find((option) => option.value === field.value) ?? null}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="visibility"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.visibility} error={fieldState.error} isOptional>
                  {({ labelId }) => (
                    <VisibilityScope {...field} inputId={labelId} onChange={(value) => setValue("visibility", value)} />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="order"
              control={control}
              rules={{ required: true, min: 1 }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.order} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      isInvalid={isInvalid}
                      readOnly={disabled}
                      decimalScale={0}
                      allowNegative={false}
                      onBlur={(value) => setValue("order", value)}
                    />
                  )}
                </FormField>
              )}
            />

            {fixings.length > 0 && (
              <FormField label={i18n.translation.common.nrOfFixingItemsRequired}>
                <ControlledNumericWithQuantity.List>
                  {fixings.map((x) => (
                    <Fragment key={x.wallMaterial.id}>
                      <ControlledNumericWithQuantity
                        item={{
                          id: x.wallMaterial.id,
                          display: x.wallMaterial.name,
                          disabled: true,
                        }}
                        controls={{
                          scale: 1,
                        }}
                        quantity={{
                          quantity: x.quantity ?? 0,
                          onUpdate: (id, quantity) =>
                            setValue(
                              "fixings",
                              fixings.reduce((rv: ItemFixingWithQuantity[], i) => {
                                if (i.wallMaterial.id === id) {
                                  return [...rv, { ...i, quantity: quantity }];
                                }
                                return [...rv, i];
                              }, []),
                            ),
                        }}
                      />
                    </Fragment>
                  ))}
                </ControlledNumericWithQuantity.List>
              </FormField>
            )}
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

export default IntermediateElementForm;
